import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { Button, Modal, Spinner } from "stories/components";
import {
  httpGetDividendUndoPreview,
  httpDeleteTransactionsByDividendInstance,
} from "services/transactions";
import { normalizeDividendUndoPreview, summarizeDividendUndoResult } from "./dividendUndo";

const formatDate = (value) => (value ? moment(value).format("D.M.YYYY") : "–");

const formatAmount = (amount, currency) =>
  `${Number(amount || 0).toLocaleString("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency || ""}`.trim();

/**
 * "Peru osinkokäsittely…" (Sovellon #2437): shows what the undo removes, per portfolio,
 * before anything is deleted, and lets the user undo it only in this portfolio or in
 * every portfolio they can see. The server (#1433) limits both to visible portfolios.
 */
const DividendUndoModal = ({ isOpen, dividendId, currentPortfolioId, onClose, onDone }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(null); // null | "this" | "all"
  const [deleteError, setDeleteError] = useState(null);
  const onlyThisRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !dividendId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setPreview(null);
    setDeleteError(null);
    setDeleting(null);
    httpGetDividendUndoPreview(dividendId).then(
      (res) => {
        if (cancelled) return;
        setPreview(normalizeDividendUndoPreview(res?.data, currentPortfolioId));
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoadError(true);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen, dividendId, currentPortfolioId]);

  // "Peru vain tästä salkusta" is the default action.
  useEffect(() => {
    if (preview && onlyThisRef.current) {
      onlyThisRef.current.focus();
    }
  }, [preview]);

  const handleDelete = (scope) => {
    if (deleting) return;
    setDeleting(scope);
    setDeleteError(null);
    const portfolioId = scope === "this" ? currentPortfolioId : undefined;
    httpDeleteTransactionsByDividendInstance(dividendId, portfolioId).then(
      (res) => {
        setDeleting(null);
        if (onDone) onDone(summarizeDividendUndoResult(res));
      },
      (error) => {
        setDeleting(null);
        // api.js hands a 403 to ApiErrorNotifier, which already tells the user why (#2260).
        if (error?.status !== 403) {
          setDeleteError(t("dividend_undo_delete_failed", "Poisto epäonnistui"));
        }
      }
    );
  };

  const handleClose = () => {
    if (deleting) return;
    if (onClose) onClose();
  };

  const portfolioCount = preview?.portfolioCount ?? 0;
  const tablePortfolios = (preview?.portfolios || []).filter((p) => p.rows.length > 0);
  const isEmpty = preview && preview.count === 0 && preview.skipMarkers.length === 0;

  const buttons = [
    <Button key="cancel" variant="secondary" onClick={handleClose} disabled={Boolean(deleting)}>
      {t("dividend_undo_modal_cancel", "Peruuta")}
    </Button>,
    <div key="actions" className="d-flex flex-wrap justify-content-end">
      {preview && portfolioCount > 1 ? (
        <Button
          variant="danger"
          className="ml-2"
          disabled={Boolean(deleting)}
          onClick={() => handleDelete("all")}
        >
          {deleting === "all" ? <Spinner className="spinner-border-sm mr-2" color="text-default" /> : null}
          {t("dividend_undo_all", "Peru kaikista salkuista ({{count}})", { count: portfolioCount })}
        </Button>
      ) : null}
      {preview ? (
        <Button
          variant="primary"
          className="ml-2"
          innerRef={onlyThisRef}
          disabled={Boolean(deleting) || !preview.currentInPreview}
          onClick={() => handleDelete("this")}
        >
          {deleting === "this" ? <Spinner className="spinner-border-sm mr-2" color="text-default" /> : null}
          {t("dividend_undo_only_this", "Peru vain tästä salkusta")}
        </Button>
      ) : null}
    </div>,
  ];

  return (
    <Modal
      title={t("dividend_undo_modal_title", "Peru osinkokäsittely")}
      isOpen={isOpen}
      closeModal={handleClose}
      buttons={buttons}
      size="modal-lg"
    >
      {loading ? (
        <div className="text-center py-3">
          <Spinner />
        </div>
      ) : null}

      {loadError ? (
        <p className="text-danger" role="alert">
          {t("dividend_undo_modal_load_error", "Peruttavien kirjausten haku epäonnistui. Yritä uudelleen.")}
        </p>
      ) : null}

      {preview ? (
        <>
          {isEmpty ? (
            <p>{t("dividend_undo_modal_empty", "Tähän osinkoon ei liity peruttavia kirjauksia.")}</p>
          ) : (
            <p>
              {t(
                "dividend_undo_modal_intro",
                "Osinkokäsittely on kirjattu alla oleviin salkkuihin. Valitse, perutaanko se vain tästä salkusta vai kaikista salkuista."
              )}
            </p>
          )}

          {!preview.currentInPreview && !isEmpty ? (
            <p className="text-muted">
              {t("dividend_undo_modal_current_missing", "Tässä salkussa ei ole peruttavia kirjauksia.")}
            </p>
          ) : null}

          {tablePortfolios.length > 0 ? (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("dividend_undo_modal_col_portfolio", "Salkku")}</th>
                  <th>{t("dividend_undo_modal_col_date", "Päivä")}</th>
                  <th className="text-right">{t("dividend_undo_modal_col_amount", "Summa")}</th>
                </tr>
              </thead>
              <tbody>
                {tablePortfolios.map((p) =>
                  p.rows.map((row, index) => (
                    <tr
                      key={`${p.id}-${row.id ?? index}`}
                      className={p.isCurrent ? "table-active" : ""}
                      data-testid={`dividend-undo-row-${p.id}`}
                    >
                      <td>
                        {index === 0 ? (
                          <>
                            {p.isCurrent ? <strong>{p.name}</strong> : p.name}
                            {p.isCurrent ? (
                              <span className="ml-1">
                                {t("dividend_undo_modal_this_portfolio", "(tämä salkku)")}
                              </span>
                            ) : null}
                          </>
                        ) : null}
                      </td>
                      <td>{formatDate(row.date)}</td>
                      <td className="text-right">{formatAmount(row.amount, row.currency)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : null}

          {preview.skipMarkers.length > 0 ? (
            <div className="mt-2" data-testid="dividend-undo-skip-markers">
              <strong>{t("dividend_undo_modal_skip_markers_title", "Ohitusmerkinnät")}</strong>
              <p className="mb-1">
                {t(
                  "dividend_undo_modal_skip_markers_text",
                  "Osinko on merkitty ohitetuksi näissä salkuissa. Peruminen poistaa myös ohitusmerkinnän."
                )}
              </p>
              <ul className="mb-0">
                {preview.skipMarkers.map((m) => (
                  <li key={m.id}>
                    {m.name}
                    {m.isCurrent ? ` ${t("dividend_undo_modal_this_portfolio", "(tämä salkku)")}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}

      {deleteError ? (
        <p className="text-danger mt-2" role="alert">
          {deleteError}
        </p>
      ) : null}
    </Modal>
  );
};

DividendUndoModal.propTypes = {
  isOpen: PropTypes.bool,
  dividendId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentPortfolioId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func,
  onDone: PropTypes.func,
};

export default DividendUndoModal;
